import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Grid, Paper, Chip, Divider, Skeleton,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import { apiGet } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import OnboardingChecklist from '../components/OnboardingChecklist';

const StatCard = ({ label, value, color, sub }) => (
  <Paper sx={{ p: 3, textAlign: 'center', height: '100%' }}>
    <Typography variant="body2" color="text.secondary" gutterBottom>{label}</Typography>
    <Typography variant="h3" color={color || 'text.primary'}>{value ?? '-'}</Typography>
    {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
  </Paper>
);

const ComparativaCard = ({ label, actual, anterior }) => {
  const diff = actual - anterior;
  const pct = anterior > 0 ? Math.round((diff / anterior) * 100) : null;
  const Icon = diff > 0 ? TrendingUpIcon : diff < 0 ? TrendingDownIcon : TrendingFlatIcon;
  const color = diff > 0 ? 'success.main' : diff < 0 ? 'error.main' : 'text.secondary';

  return (
    <Paper sx={{ p: 3, textAlign: 'center', height: '100%' }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>{label}</Typography>
      <Typography variant="h3">{actual ?? '-'}</Typography>
      {anterior != null && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.5 }}>
          <Icon sx={{ fontSize: 16, color }} />
          <Typography variant="caption" color={color}>
            {diff >= 0 ? '+' : ''}{diff} vs mes anterior
            {pct !== null ? ` (${pct >= 0 ? '+' : ''}${pct}%)` : ''}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const STATE_COLORS = {
  atendido: '#1565c0',
  confirmado: '#ffb300',
  pendiente: '#ef5350',
  cancelado: '#616161',
  ausente: '#ff6d00',
  libre: '#546e7a',
  ocupado: '#546e7a',
};

const AdminMetrics = ({ data }) => {
  const ausentismoColor = data.tasa_ausentismo > 20 ? 'error' : data.tasa_ausentismo > 10 ? 'warning' : 'success';

  return (
    <Box>
      {/* Fila 1: métricas globales */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <ComparativaCard
            label="Turnos este mes"
            actual={data.turnos_mes}
            anterior={data.turnos_mes_anterior}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <StatCard label="Turnos esta semana" value={data.turnos_semana} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <StatCard label="Pacientes activos" value={data.pacientes_total} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <StatCard label="Pacientes nuevos (mes)" value={data.pacientes_nuevos_mes} color="success.main" />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <StatCard label="Usuarios de la clínica" value={data.usuarios_activos} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Paper sx={{ p: 3, textAlign: 'center', height: '100%' }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>Ausentismo (mes)</Typography>
            <Typography variant="h3">{data.tasa_ausentismo ?? 0}%</Typography>
            <Chip
              label={`${data.ausentes_mes ?? 0} ausentes / ${(data.ausentes_mes ?? 0) + (data.atendidos_mes ?? 0)} finalizados`}
              color={ausentismoColor}
              size="small"
              sx={{ mt: 0.5, fontSize: '0.65rem' }}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Fila 2: ocupación por médico */}
      {data.ocupacion_por_medico?.length > 0 && (
        <Paper sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Turnos por médico — mes actual</Typography>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.ocupacion_por_medico} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <XAxis dataKey="medico" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} />
              <Tooltip formatter={(v) => [v, 'Turnos']} />
              <Bar dataKey="turnos" fill="#1565c0" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Fila 3: actividad por entidad */}
      {data.audit_por_entidad?.length > 0 && (
        <Paper sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Actividad últimos 30 días (por entidad)</Typography>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.audit_por_entidad} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <XAxis dataKey="entidad" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="total" fill="#78909c" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Fila 4: motivos de cancelación */}
      {data.top_motivos_cancelacion?.length > 0 && (
        <Paper sx={{ p: 2.5 }}>
          <Typography variant="h6" gutterBottom>
            Motivos de cancelación — mes actual
            <Chip label={`${data.cancelados_mes ?? 0} cancelados`} size="small" sx={{ ml: 1.5 }} />
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {data.top_motivos_cancelacion.map((m) => (
              <Box key={m.motivo} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Chip label={m.count} size="small" color="default" sx={{ minWidth: 32, fontWeight: 700 }} />
                <Typography variant="body2">{m.motivo}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

const RecepMetrics = ({ data }) => (
  <Box>
    <Grid container spacing={2.5} sx={{ mb: 3 }}>
      <Grid size={{ xs: 6, sm: 4 }}>
        <StatCard label="Turnos hoy" value={data.turnos_hoy} color="primary.main" />
      </Grid>
      <Grid size={{ xs: 6, sm: 4 }}>
        <StatCard label="Turnos esta semana" value={data.turnos_semana} />
      </Grid>
      <Grid size={{ xs: 6, sm: 4 }}>
        <StatCard label="Pacientes nuevos (mes)" value={data.pacientes_nuevos_mes} color="success.main" />
      </Grid>
    </Grid>

    {data.estados_mes?.length > 0 && (
      <Paper sx={{ p: 2.5 }}>
        <Typography variant="h6" gutterBottom>Turnos del mes por estado</Typography>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data.estados_mes} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <XAxis dataKey="estado" tick={{ fontSize: 12 }} />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Bar dataKey="total" radius={[4, 4, 0, 0]}>
              {data.estados_mes.map((entry) => (
                <Cell key={entry.estado} fill={STATE_COLORS[entry.estado] || '#9e9e9e'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Paper>
    )}
  </Box>
);

const MedicoMetrics = ({ data }) => {
  if (data.sin_doctor) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Tu usuario no tiene un médico vinculado. Pedile al administrador que lo configure.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard label="Mis turnos esta semana" value={data.turnos_semana} />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard label="Mis turnos este mes" value={data.turnos_mes} />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard label="Pacientes distintos (mes)" value={data.pacientes_distintos_mes} color="primary.main" />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard label="HC creadas este mes" value={data.registros_clinicos_mes} color="success.main" />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        {data.weekly_chart?.length > 0 && (
          <Grid size={{ xs: 12, md: data.top_diagnosticos?.length > 0 ? 7 : 12 }}>
            <Paper sx={{ p: 2.5, height: '100%' }}>
              <Typography variant="h6" gutterBottom>Atendidos por semana</Typography>
              {data.tasa_asistencia != null && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Tasa de asistencia del mes:{' '}
                  <Typography component="span" fontWeight={700}
                    color={data.tasa_asistencia >= 80 ? 'success.main' : 'warning.main'}>
                    {data.tasa_asistencia}%
                  </Typography>
                </Typography>
              )}
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.weekly_chart} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <XAxis dataKey="semana" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip formatter={(v) => [v, 'Atendidos']} />
                  <Bar dataKey="atendidos" fill="#1565c0" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        )}

        {data.top_diagnosticos?.length > 0 && (
          <Grid size={{ xs: 12, md: data.weekly_chart?.length > 0 ? 5 : 12 }}>
            <Paper sx={{ p: 2.5, height: '100%' }}>
              <Typography variant="h6" gutterBottom>Top diagnósticos del mes</Typography>
              <Box sx={{ mt: 2 }}>
                {data.top_diagnosticos.map(({ diagnostico, count }) => (
                  <Box key={diagnostico} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.25 }}>
                    <Chip label={count} size="small" color="primary" sx={{ minWidth: 32, fontWeight: 700 }} />
                    <Typography variant="body2">{diagnostico}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

const ROLE_TITLES = {
  admin: 'Panel de administración',
  recepcionista: 'Resumen de recepción',
  medico: 'Mis estadísticas',
};

const Metrics = () => {
  const { role } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet('/metrics/').then(r => setMetrics(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={260} height={36} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width={340} sx={{ mb: 3 }} />
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {[0, 1, 2, 3].map(i => (
            <Grid size={{ xs: 6, sm: 4, md: 2 }} key={i}>
              <Skeleton variant="rounded" height={110} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={240} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>
          {ROLE_TITLES[role] || 'Métricas'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {new Date().toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />

      {role === 'admin' && <OnboardingChecklist />}

      {metrics?.role === 'admin' && <AdminMetrics data={metrics} />}
      {metrics?.role === 'recepcionista' && <RecepMetrics data={metrics} />}
      {metrics?.role === 'medico' && <MedicoMetrics data={metrics} />}
      {!metrics && (
        <Typography color="text.secondary">No se pudieron cargar las métricas.</Typography>
      )}
    </Box>
  );
};

export default Metrics;
